import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft, Lock, BarChart3, Users } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import LogoMark from "../../components/LogoMark";

export default function AdminLogin() {
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/admin/login", { username: u, password: p });
      login(r.data.token, r.data.user);
      toast.success("Welcome back");
      navigate("/admin");
    } catch (e) { toast.error(e.response?.data?.detail || "Login failed"); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-[#F8F4E9]">
      {/* Decorative left (3 cols) */}
      <div className="relative hidden lg:flex lg:col-span-3 flex-col justify-between p-10 overflow-hidden text-white"
        style={{ background: "linear-gradient(160deg, #1F1816 0%, #2B1010 35%, #7A1010 70%, #D71921 100%)" }}>
        <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#D71921]/30 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 w-[24rem] h-[24rem] rounded-full bg-[#B8862A]/20 blur-3xl" />

        <Link to="/" className="relative inline-flex items-center gap-1 text-sm text-white/70 hover:text-white">
          <ArrowLeft className="w-4 h-4" /> Public site
        </Link>

        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F2C97A]/15 backdrop-blur border border-[#F2C97A]/30 text-[#F2C97A] text-xs font-bold uppercase tracking-widest mb-6">
            Admin Console
          </div>
          <h2 className="font-display text-5xl xl:text-6xl leading-[1.05] tracking-tight">
            Operations.<br />Insights.<br />Control.
          </h2>
          <p className="mt-5 text-white/75 text-base leading-relaxed max-w-md">
            Approve KYC, manage lots, process sales, and monitor every animal — all from one console.
          </p>

          <div className="mt-10 grid grid-cols-3 gap-3 max-w-md">
            <Card icon={Users} label="Customers" value="1,200+" />
            <Card icon={Lock} label="Secure" value="JWT + RBAC" />
            <Card icon={BarChart3} label="Reports" value="Excel + PDF" />
          </div>
        </div>

        <div className="relative text-xs text-white/50">© 2026 PassiveIearn — Internal use only</div>
      </div>

      {/* RIGHT form (2 cols) */}
      <div className="lg:col-span-2 flex items-center justify-center p-6 md:p-10 relative">
        <Link to="/" className="lg:hidden absolute top-4 left-4 text-sm text-[#6B5F58] flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <LogoMark size="lg" className="mx-auto mb-3" />
            <div className="text-sm text-[#6B5F58] uppercase tracking-widest font-semibold">Admin Console</div>
          </div>

          <form className="gf-card p-7 space-y-4 shadow-xl shadow-[#1F1816]/10" onSubmit={submit}>
            <label className="block">
              <span className="text-sm font-medium">Username</span>
              <input data-testid="admin-username-input" autoFocus
                className="mt-1 w-full rounded-xl border border-[#E8E0D2] px-3 py-3 outline-none focus:border-[#D71921] focus:ring-2 focus:ring-[#D71921]/15"
                value={u} onChange={(e) => setU(e.target.value)} />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Password</span>
              <input data-testid="admin-password-input" type="password"
                className="mt-1 w-full rounded-xl border border-[#E8E0D2] px-3 py-3 outline-none focus:border-[#D71921] focus:ring-2 focus:ring-[#D71921]/15"
                value={p} onChange={(e) => setP(e.target.value)} />
            </label>
            <button type="submit" data-testid="admin-login-btn" disabled={loading}
              className="w-full px-6 py-3.5 rounded-xl text-white font-semibold disabled:opacity-60 shadow-lg shadow-[#D71921]/25 hover:-translate-y-0.5 transition-all"
              style={{ background: "linear-gradient(135deg, #D71921 0%, #B0151E 100%)" }}>
              {loading ? "Signing in..." : "Sign in to Console"}
            </button>
            <div className="text-center text-xs text-[#6B5F58]">
              Customer? <Link to="/login" className="text-[#D71921] underline font-medium">Sign in here</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value }) {
  return (
    <div className="bg-white/8 backdrop-blur rounded-xl p-3 border border-white/15">
      <Icon className="w-4 h-4 text-[#F2C97A] mb-2" />
      <div className="text-[10px] uppercase tracking-widest text-white/60">{label}</div>
      <div className="text-sm font-semibold mt-0.5">{value}</div>
    </div>
  );
}
